import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DevicePhoneMobileIcon,
  EyeIcon,
  GlobeAltIcon,
  MapPinIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import ScrollReveal from './ScrollReveal';
import { useLanguage } from '../LanguageContext';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.rightoz.user';
const IOS_APP_URL =
  'https://apps.apple.com/in/app/rightoz-consumer/id6763323665';
const WEB_APP_URL = 'https://www.consumer.rightoz.in/';

const slidesEn = Array.from({ length: 10 }, (_, index) => ({
  src: `/story-slides/panel_${index + 1}.png`,
}));

const slidesHi = Array.from({ length: 10 }, (_, index) => ({
  src: `/story-slides-hi/panel_${index + 1}.png`,
}));

const productsEn = [
  { name: 'Cow Milk', price: '₹63' },
  { name: 'Desi Cow A2', price: '₹87' },
  { name: 'A2 Buffalo', price: '₹91' },
];

const productsHi = [
  { name: 'गाय का दूध', price: '₹63' },
  { name: 'देसी गाय A2', price: '₹87' },
  { name: 'A2 भैंस', price: '₹91' },
];

const appBenefitsEn = [
  { icon: CalendarDaysIcon, label: 'Twice-a-day delivery' },
  { icon: PlusCircleIcon, label: 'Skip or ask for extra' },
  { icon: EyeIcon, label: 'See deliveries, balance & payments' },
];

const appBenefitsHi = [
  { icon: CalendarDaysIcon, label: 'दिन में दो बार डिलीवरी' },
  { icon: PlusCircleIcon, label: 'रोकें या अतिरिक्त मँगाएँ' },
  { icon: EyeIcon, label: 'डिलीवरी, बैलेंस और भुगतान देखें' },
];

const Hero: React.FC = () => {
  const { language } = useLanguage();
  const isHindi = language === 'hi';
  const slides = isHindi ? slidesHi : slidesEn;
  const products = isHindi ? productsHi : productsEn;
  const appBenefits = isHindi ? appBenefitsHi : appBenefitsEn;
  const [activeSlide, setActiveSlide] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(
      () => setActiveSlide((current) => (current + 1) % slides.length),
      8000,
    );
    return () => window.clearInterval(timer);
  }, [slides.length]);

  useEffect(() => {
    const nextSlide = slides[(activeSlide + 1) % slides.length];
    const timer = window.setTimeout(() => {
      const image = new Image();
      image.decoding = 'async';
      image.src = nextSlide.src;
    }, 400);
    return () => window.clearTimeout(timer);
  }, [activeSlide, isHindi, slides]);

  const showPrevious = () =>
    setActiveSlide((current) => (current - 1 + slides.length) % slides.length);
  const showNext = () =>
    setActiveSlide((current) => (current + 1) % slides.length);

  return (
    <section
      id="home"
      className="relative overflow-hidden bg-white pb-14 pt-24 text-[var(--color-forest)] md:pb-20 md:pt-28"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(76,175,80,0.12),_transparent_32%),radial-gradient(circle_at_bottom_right,_rgba(27,94,32,0.07),_transparent_28%)]" />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(390px,0.82fr)] lg:items-stretch">
          <ScrollReveal direction="left" delayMs={40} className="min-w-0">
            <div
              className="relative mx-auto flex aspect-[3/5] w-full max-w-[350px] items-center justify-center overflow-hidden rounded-[2rem] border border-[rgba(27,94,32,0.12)] bg-[#f7f6f0] shadow-[0_20px_60px_rgba(27,94,32,0.1)] sm:max-w-[390px] lg:max-w-[440px]"
              role="region"
              aria-roledescription="carousel"
              aria-label={isHindi ? 'Rightoz की कहानी' : 'The Rightoz story'}
              onKeyDown={(event) => {
                if (event.key === 'ArrowLeft') showPrevious();
                if (event.key === 'ArrowRight') showNext();
              }}
              onTouchStart={(event) => {
                touchStartX.current = event.touches[0]?.clientX ?? null;
              }}
              onTouchEnd={(event) => {
                if (touchStartX.current === null) return;
                const distance = event.changedTouches[0].clientX - touchStartX.current;
                touchStartX.current = null;
                if (Math.abs(distance) < 45) return;
                if (distance < 0) showNext();
                else showPrevious();
              }}
              style={{ touchAction: 'pan-y' }}
              tabIndex={0}
            >
              <img
                src={slides[activeSlide].src}
                alt={isHindi
                  ? `Rightoz दूध की कहानी, 10 में से स्लाइड ${activeSlide + 1}`
                  : `Rightoz milk story, slide ${activeSlide + 1} of 10`}
                className="block h-full w-full object-cover"
                decoding="async"
              />

              <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-4 bg-gradient-to-t from-black/55 via-black/15 to-transparent px-4 pb-4 pt-16 sm:pb-6">
                <button
                  type="button"
                  onClick={showPrevious}
                  className="hidden h-11 w-11 items-center justify-center rounded-full bg-white/80 text-[var(--color-forest)] shadow-lg backdrop-blur-sm transition hover:scale-105 hover:bg-white/95 lg:flex"
                  aria-label={isHindi ? 'पिछली स्लाइड' : 'Previous slide'}
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>

                <div className="flex flex-wrap justify-center gap-1.5">
                  {slides.map((slide, index) => (
                    <button
                      key={slide.src}
                      type="button"
                      onClick={() => setActiveSlide(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === activeSlide ? 'w-8 bg-white' : 'w-2.5 bg-white/55 hover:bg-white/80'
                      }`}
                      aria-label={`${isHindi ? 'स्लाइड' : 'Slide'} ${index + 1}`}
                      aria-current={index === activeSlide ? 'true' : undefined}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={showNext}
                  className="hidden h-11 w-11 items-center justify-center rounded-full bg-white/80 text-[var(--color-forest)] shadow-lg backdrop-blur-sm transition hover:scale-105 hover:bg-white/95 lg:flex"
                  aria-label={isHindi ? 'अगली स्लाइड' : 'Next slide'}
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>

            </div>
            <a
              href="#milk-details-card"
              className="mx-auto mt-3 flex w-fit items-center gap-1 text-xs font-semibold text-[var(--color-forest)] lg:hidden"
            >
              {isHindi ? 'नीचे और देखें' : 'More below'}
              <ChevronDownIcon className="h-4 w-4 animate-bounce" />
            </a>
          </ScrollReveal>

          <ScrollReveal direction="right" delayMs={100} className="min-w-0">
            <div id="milk-details-card" className="flex h-full min-w-0 scroll-mt-24 flex-col rounded-[2rem] bg-[var(--color-forest)] p-6 text-white shadow-[0_20px_60px_rgba(27,94,32,0.18)]">
              <p className="inline-flex w-fit items-center rounded-full bg-white/10 px-3 py-2 text-sm font-semibold text-[#e3eddc]">
                <MapPinIcon className="mr-2 h-4 w-4" />
                {isHindi ? 'अभी जयपुर में उपलब्ध' : 'Now available in Jaipur'}
              </p>

              <h1 className="mt-4 font-display text-3xl leading-[1.05] text-[var(--color-cream)] sm:text-4xl">
                {isHindi ? 'ताज़ा दूध, सही तरीके से आपके घर।' : 'Fresh milk, delivered right'}
              </h1>
              <p className="mt-3 text-lg font-bold text-[#f2d58a]">
                {isHindi ? 'सिर्फ भरोसा नहीं, प्रमाण भी।' : 'Not just trust, but proof too'}
              </p>
              <p className="mt-3 text-sm leading-6 text-[#d7e2d0]">
                {isHindi
                  ? 'किसान से घर तक दूध का सफर देखें और अपनी रोज़ की डिलीवरी ऐप से संभालें।'
                  : 'Follow your milk from farm to home, then manage your everyday delivery in the app'}
              </p>

              <div className="mt-4 grid grid-cols-3 gap-2">
                {products.map((product) => (
                  <div key={product.name} className="rounded-2xl border border-white/10 bg-white/5 p-2.5">
                    <p className="text-[11px] leading-4 text-[#c9d9c0]">{product.name}</p>
                    <p className="mt-1 font-display text-2xl font-black text-[#f2d58a]">{product.price}</p>
                    <p className="text-[10px] uppercase tracking-wide text-[#afc7a2]">
                      {isHindi ? 'प्रति लीटर' : 'per litre'}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-4 space-y-2">
                {appBenefits.map((benefit) => (
                  <div key={benefit.label} className="flex items-center gap-3 rounded-2xl bg-white/5 px-4 py-2">
                    <benefit.icon className="h-5 w-5 shrink-0 text-[#cfe0c5]" />
                    <span className="text-sm text-[#edf3e9]">{benefit.label}</span>
                  </div>
                ))}
              </div>

              <div id="app-download-buttons" className="mt-auto scroll-mt-28 pt-4">
                <p className="mb-3 text-sm font-semibold text-white">
                  {isHindi ? 'Rightoz ऐप डाउनलोड करें' : 'Download the Rightoz app'}
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-glow cta-glow-light inline-flex items-center justify-center rounded-full border-2 border-white bg-white px-4 py-3 text-sm font-extrabold text-[var(--color-forest)] shadow-[0_8px_24px_rgba(255,255,255,0.2)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(255,255,255,0.3)]"
                  >
                    <DevicePhoneMobileIcon className="mr-2 h-5 w-5" />
                    Google Play
                  </a>
                  <a
                    href={IOS_APP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-glow cta-glow-light inline-flex items-center justify-center rounded-full border-2 border-[#f8e4aa] bg-[#f2d58a] px-4 py-3 text-sm font-extrabold text-[#493807] shadow-[0_8px_24px_rgba(242,213,138,0.28)] transition hover:-translate-y-0.5 hover:bg-[#f7df9b] hover:shadow-[0_12px_30px_rgba(242,213,138,0.38)]"
                  >
                    <DevicePhoneMobileIcon className="mr-2 h-5 w-5" />
                    App Store
                  </a>
                </div>
                <a
                  href={WEB_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center text-sm font-semibold text-[#d7e2d0] transition hover:text-white"
                >
                  <GlobeAltIcon className="mr-2 h-4 w-4" />
                  {isHindi ? 'बिना डाउनलोड ब्राउज़र में खोलें' : 'Or open in your browser'}
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          </ScrollReveal>
        </div>

      </div>
    </section>
  );
};

export default Hero;
